import { type CSSProperties, useEffect, useRef, useState } from "react";

// `floor`: an independent minimum for the popover, in px. A trigger that
// shrink-fits its selected label can be far narrower than its own rows need —
// slaving the menu to it truncates them to a few characters. When a floor is
// given the menu is `max(trigger, floor)` wide: it still grows with a wide
// trigger, but never collapses below a legible width.
export function useTriggerWidth<T extends HTMLElement>(
  isOpen?: boolean,
  floor?: number
) {
  const triggerRef = useRef<T>(null);
  const [triggerWidth, setTriggerWidth] = useState<number>(0);

  useEffect(() => {
    if (isOpen === false) return;
    if (!triggerRef.current) return;
    setTriggerWidth(triggerRef.current.offsetWidth);
  }, [isOpen]);

  const effectiveWidth =
    triggerWidth > 0 && floor ? Math.max(triggerWidth, floor) : triggerWidth;

  // `widthMode="trigger"` sizes by class, and this build's utilities are
  // `!important`, so the inline width alone cannot raise it. The floor is
  // handed to that class as a variable; `max()` stays in the class because
  // jsdom throws on it inline.
  const floorStyle = floor
    ? ({ "--selector-menu-floor": `${floor}px` } as CSSProperties)
    : undefined;
  const triggerWidthStyle: CSSProperties | undefined =
    effectiveWidth > 0
      ? {
          width: `${effectiveWidth}px`,
          minWidth: `${effectiveWidth}px`,
          ...floorStyle,
        }
      : floorStyle;

  return {
    triggerRef,
    triggerWidth,
    triggerWidthStyle,
  };
}
